import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Star, Trophy, Clock, ArrowRight } from 'lucide-react';
import type { SteamUser, UserGameStats } from '../types';
import { calculateBadges, calculateTotalExperience, calculateUserLevel, calculateProgress, LEVELS } from '../utils/badges';

interface UserProfileHeaderProps {
  user: SteamUser;
  gameStats: UserGameStats;
}

export function UserProfileHeader({ user, gameStats }: UserProfileHeaderProps) {
  const totalPlaytimeHours = Math.floor(gameStats.totalPlaytime / 60);
  const totalAchievements = gameStats.achievementStats.unlocked;
  const earnedBadges = calculateBadges(totalPlaytimeHours, totalAchievements);
  const experience = calculateTotalExperience(earnedBadges);
  const levelInfo = calculateUserLevel(experience);
  const progress = calculateProgress(experience);
  const nextLevel = LEVELS[levelInfo.level] || LEVELS[LEVELS.length - 1];

  return (
    <div className="relative overflow-hidden rounded-lg shadow-xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
        <div className="absolute -top-32 -right-32 w-64 h-64 bg-purple-500 rounded-full opacity-20 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-indigo-500 rounded-full opacity-20 blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="relative px-8 py-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          {/* Avatar and Basic Info */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-xl overflow-hidden ring-4 ring-white/20 shadow-lg">
                <img
                  src={user.avatarfull}
                  alt={user.personaname}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-1.5 shadow-lg">
                <Shield className="w-5 h-5 text-white" />
              </div>
            </div>

            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                {user.personaname}
              </h1>
              {user.realname && (
                <p className="text-indigo-200 text-lg">{user.realname}</p>
              )}
            </div>
          </div>

          {/* Level and Stats */}
          <div className="flex-grow">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              {/* Level Progress */}
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <Star className="w-8 h-8 text-yellow-300" />
                  </div>
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-bold text-white">
                    {levelInfo.level}
                  </span>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-white font-semibold">{levelInfo.title}</span>
                    <span className="text-indigo-200">•</span>
                    <span className="text-indigo-200">{experience.toLocaleString()} XP</span>
                  </div>
                  <div className="w-48">
                    <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-yellow-300 to-yellow-500 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-xs text-indigo-200">
                      <span>{experience.toLocaleString()}</span>
                      <span>{nextLevel.experienceRequired.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="flex items-center gap-6">
                <Link
                  to={`/user/${user.steamid}/badges`}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 transition-colors group"
                >
                  <Trophy className="w-5 h-5 text-yellow-300" />
                  <div>
                    <div className="text-sm text-indigo-200">Badges Earned</div>
                    <div className="text-white font-semibold">{earnedBadges.length}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-indigo-200 transform translate-x-0 group-hover:translate-x-1 transition-transform" />
                </Link>

                <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-white/10">
                  <Clock className="w-5 h-5 text-indigo-300" />
                  <div>
                    <div className="text-sm text-indigo-200">Total Playtime</div>
                    <div className="text-white font-semibold">
                      {totalPlaytimeHours.toLocaleString()} hours
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}